import React, { useState, useContext, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
//import { format } from 'date-fns';
//import Globe3D from './Globe3D';
import db from '../firebase';
import OpenAI from 'openai';
import { MetadataContext } from '../contexts/MetadataContext';
import { Button, CircularProgress, Paper, Typography } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import DOMPurify from 'dompurify';  // Add this import for safety
import { useAuth } from '../contexts/AuthContext';  // Add this import
import { FeedbackContext } from '../contexts/FeedbackContext';  // Add this import
import { jsonrepair } from 'jsonrepair';  // Add this import for JSON repair
import TurndownService from 'turndown';

const cleanAndRepairJson = (jsonString) => {
    try {
        // Step 1: Replace all newlines and extra spaces
        let cleaned = jsonString.replace(/[\n\r\t]/g, ' ').replace(/\s+/g, ' ');

        // Step 2: Clean URLs in href attributes
        cleaned = cleaned.replace(/href="([^"]+)"/g, (match, url) => {
            // Remove any quotes from URLs and escape properly
            const cleanUrl = url.replace(/["']/g, '');
            return `href=\\"${cleanUrl}\\"`;
        });

        // Step 3: Fix remaining HTML attributes and quotes
        cleaned = cleaned
            // Fix class attributes
            .replace(/class="([^"]*)"/g, 'class=\\\"$1\\\"')
            // Fix any remaining unescaped quotes within HTML content
            .replace(/<[^>]*>/g, match =>
                match.replace(/([^\\])"/g, '$1\\"')
            );

        // Step 4: Use jsonrepair as final step
        const repaired = jsonrepair(cleaned);

        // Validate by parsing
        JSON.parse(repaired);

        return repaired;
    } catch (error) {
        console.error('JSON repair failed:', error);
        throw new Error(`JSON repair failed: ${error.message}`);
    }
};

const validateNewsletterData = (data) => {
    const requiredFields = ['welcome', 'breaking_news', 'beyond_the_feed', 'selected_long_read', 'to_close'];
    const missingFields = requiredFields.filter(field => !data[field]);

    if (missingFields.length > 0) {
        console.log('Newsletter data validation:', {
            present: Object.keys(data),
            missing: missingFields,
            data: data // Log full data for inspection
        });
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Check for empty content
    const emptyFields = requiredFields.filter(field =>
        data[field]?.trim().length === 0
    );

    if (emptyFields.length > 0) {
        throw new Error(`Empty content in fields: ${emptyFields.join(', ')}`);
    }

    return true;
};

const NewsletterGenerator = () => {
    const [loading, setLoading] = useState(false);
    const [newsletter, setNewsletter] = useState('');
    const { metadata } = useContext(MetadataContext);
    const [markers, setMarkers] = useState([]);
    const [sendingEmail, setSendingEmail] = useState(false);
    const { currentUser } = useAuth();  // Add this line
    const { showFeedback } = useContext(FeedbackContext);

    const OPENAI_API_KEY = metadata.OPENAI_API_KEY || "";

    const openai = new OpenAI({
        apiKey: OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
    });

    // This is a list of domains that we want to exclude from the newsletter generation process.
    const EXCLUDED_DOMAINS = [
        'youtube.com',
        'youtu.be',
        'wattsupwiththat.com',
        'climatechangedispatch.com',
        // Add other domains to exclude
    ];

    const fetchTopArticles = async () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        const articlesRef = collection(db, 'articles');
        const q = query(
            articlesRef,
            where('pubDate', '>=', yesterday),
            limit(150)
        );

        const querySnapshot = await getDocs(q);
        // Filter and process articles
        const articles = querySnapshot.docs
            .map(doc => ({
                id: doc.id,
                //vote: 0, // Default vote value
                //insightfullCount: 0, // Default insight count
                ...doc.data()
            }))
            .filter(article => {
                // Check if article has TLDR
                const hasTLDR = article.TLDR && article.TLDR !== "TL;DR not available";

                // Check if URL is not from excluded domains
                const url = article.link.toLowerCase();
                const isAllowedDomain = !EXCLUDED_DOMAINS.some(domain =>
                    url.includes(domain.toLowerCase())
                );

                return hasTLDR && isAllowedDomain;
            })
            .sort((a, b) => {
                // Sort by votes and insightful count
                const aScore = (a.vote || 0) + (a.insightfullCount || 0);
                const bScore = (b.vote || 0) + (b.insightfullCount || 0);
                return bScore - aScore;
            });
        console.log(articles);
        console.log("articles length: ", articles.length);
        return articles;
    };

    const generateNewsletter = async () => {
        setLoading(true);
        try {
            const articles = await fetchTopArticles();

            if (articles.length === 0) {
                showFeedback('No valid articles found in the last 24 hours', 'warning');
                return;
            }

            const articlesSummary = articles.map(article => `
            Title: ${article.title}
            URL: ${article.link}
            Votes: ${article.vote || 0}
            TLDR: ${article.TLDR}
            Category: ${article.category || 'No category'}
            InsightfulCount: ${article.insightfullCount || 0}
        `).join('\n\n');
            console.log(articlesSummary);
            const newsletterPrompt = `
You are an assistant helping to create a daily climate newsletter called *The Climate Spectrum*. The tone should be accessible, insightful, and lightly conversational—suitable for a general audience interested in climate change and sustainability.

You will be given:
- A list of breaking news items (5 to 20 titles with links and optional summaries)
- A list of Beyond the Feed articles (3 to 10 titles with links and optional descriptions)
- One selected long read (title, link, and optional summary)

---
Return ONLY a valid JSON object with NO additional text or formatting, using the following fields:

{
  "welcome": "[string - 2 to 3 sentence abstract for the intro]",
  "breaking_news": "[string - HTML-formatted list of 8 to 10 top headlines, each with a short note and hyperlink integrated in the text]",
  "beyond_the_feed": "[string - HTML-formatted list of 4 to 6 thoughtful or underreported articles with short hooks, links integrated in the text]",
  "selected_long_read": "[string - 1 paragraph summary/teaser for the chosen long read, include title and link]",
  "to_close": "[string - short ending message encouraging reflection, motivation, or sharing]"
}

---

**Format guidelines:**
Important: Return a valid JSON object with properly escaped HTML content. Ensure all HTML attributes use escaped quotes (\\" not ")
Make sure:
- All URLs in href attributes are properly escaped
- All HTML tags are properly closed
- No trailing commas in JSON
- No line breaks within JSON strings
- Keep tone light, smart, and inviting

If the number of items is too long, select the best ones based on relevance, depth, or insight.
 Articles to summarize:
            ${articlesSummary}
`;

            const response = await openai.chat.completions.create({
                model: "gpt-4o-mini",
                messages: [
                    {
                        "role": "user",
                        "content": newsletterPrompt
                    }
                ],
                temperature: 0.7,
                max_tokens: 2000  // Increase from 1000 to 2000
            });
            console.log("Newsletter prompt: ", newsletterPrompt);
            const rawResponse = response.choices[0].message.content;
            console.log("Raw GPT response:", rawResponse);

            // Try parsing before setting state to catch issues early
            const parsed = JSON.parse(cleanAndRepairJson(rawResponse));
            console.log("Parsed response fields:", Object.keys(parsed));

            setNewsletter(rawResponse);
        } catch (error) {
            console.error('Error generating newsletter:', error);
            showFeedback('Failed to generate complete newsletter', 'error');
        } finally {
            setLoading(false);
        }
    };

    const generateTrendingNewsletter = async () => {
        setLoading(true);
        try {
            const articles = await fetchTopArticles();

            if (!articles || articles.length === 0) {
                showFeedback('No valid articles found in the last 24 hours', 'warning');
                return;
            }

            // Format articles in actual JSON array (not a string block)
            const articleList = articles.map(article => ({
                title: article.title,
                summary: article.TLDR,
                url: article.link
            }));

            const systemPrompt = `
You are a sharp, editorial-style sustainability newsletter editor.
Your writing is clear, engaging, and narrative-driven. You prefer short, punchy paragraphs with smooth transitions and an accessible, modern tone.
You avoid academic jargon and long-winded metaphors. Instead, you write like you're talking to an intelligent but busy reader.
Each newsletter has a theme (e.g., “data droughts,” “climate silence,” “hope in small steps”), and connects articles using wit, clarity, and storytelling.
Always include hyperlinks naturally in the sentence, not as raw URLs.
Think: part journalist, part columnist — never a speechwriter.
`;

            const userPrompt = `You will receive a JSON list of articles. Each article includes:
    - title
    - summary
    - url
    
    Your job:
1. Pick a unifying theme.
2. Write a short newsletter (max. 500 words) that:
   - starts with a bold or thoughtful opening sentence
   - flows across 4–6 short paragraphs
   - weaves in the articles using plain, lively English
   - includes at least one hyperlink per paragraph
   - ends with a call to action or a note of hope

Style guide:
- Write like a modern editor, not a scientist or politician.
- Use clear, active sentences and human phrasing.
- Avoid clichés, passive voice, or abstract metaphors.
- Don't explain everything — trust the reader’s intelligence.
- Be sharp, not serious. Inviting, not grandiose.
    
    
    ${JSON.stringify(articleList, null, 2)}
    Here is an example of the desired output:
    
    🌪️ **newsletter: when measuring becomes inconvenient**
    
**\n\nThe thermometer is rising, but some governments would rather toss the gauge out the window than turn down the heat. In the U.S., NOAA — once the gold standard for climate impact tracking — has decided to [stop recording the costs of extreme weather](https://www.nytimes.com/2025/05/06/climate/noaa-climate-disaster-costs.html). The “billion dollar disasters” are quietly disappearing from the radar. If you stop measuring it, maybe it ceases to exist.\n\nBut the climate doesn’t care about budget constraints. In [Kalamazoo County](https://nowkalamazoo.org/2025/05/extreme-weather-preparation/), where tornadoes and floods are becoming more intense, local utilities and governments are investing in resilience. Even without a federal database, residents know exactly what’s coming.\n\nThe same holds true for fishers along the [Gulf of Guinea](https://salatainstitute.harvard.edu/fishers-gulf-guinea-income-diversification/), where declining catches are forcing entire communities to reconsider their future. They can’t shut down the statistics—they have to rewrite their reality.\n\nRewriting is also happening closer to home. In Chicago, a [closed-loop facility](https://techxplore.com/news/2025-05-chicago-campus-composts-energy-redefine.html) is turning food waste into both compost and clean energy, proving that sustainable waste management is more than theory — it’s a working system with tangible results.\n\nWhich brings us to the question that looms largest: if data goes silent, who will speak for the future? Perhaps it’s today’s children, who — according to recent research — are expected to face up to [seven times more climate extremes](https://cosmosmagazine.com/nature/climate/kids-climate-extremes/) than their grandparents. The more we act to protect their future, the less we’ll need to measure what went wrong.\n\n---\n\
    
    Now generate a newsletter based on this article list}
    `;

            const response = await openai.chat.completions.create({
                model: "gpt-4o",
                messages: [
                    { role: "system", content: systemPrompt },
                    { role: "user", content: userPrompt }
                ],
                temperature: 0.7,
                max_tokens: 2000
            });

            const rawResponse = response.choices[0].message.content;
            console.log("Raw GPT response:", rawResponse);

            setNewsletter(rawResponse);  // Store natural text
        } catch (error) {
            console.error('Error generating newsletter:', error);
            showFeedback('Failed to generate complete newsletter', 'error');
        } finally {
            setLoading(false);
        }
    };


    const sendNewsletter = async () => {
        setSendingEmail(true);
        try {
            const repairedJson = cleanAndRepairJson(newsletter);
            const newsletterData = JSON.parse(repairedJson);

            // Validate content
            validateNewsletterData(newsletterData);

            // Prepare data for Firebase function
            const emailData = {
                ...newsletterData,
                userEmail: currentUser.email,
                displayName: currentUser.displayName || 'User',
            };
            console.log('Final email data:', emailData);

            // Step 5: Send email
            const functions = getFunctions();
            const sendNewsletterEmail = httpsCallable(functions, 'sendNewsletterEmail');
            const result = await sendNewsletterEmail(emailData);

            console.log('Email function result:', result);
            showFeedback('Newsletter sent successfully!', 'success');
        } catch (error) {
            console.error('Newsletter processing failed:', error);
            showFeedback(`Error: ${error.message}`, 'error');
        } finally {
            setSendingEmail(false);
        }
    };

    const sendToTeams = async () => {
        const turndown = new TurndownService();
        try {
            const repairedJson = cleanAndRepairJson(newsletter);
            const newsletterData = JSON.parse(repairedJson);

            // Validate content
            validateNewsletterData(newsletterData);

            // Convert HTML breaking news to FactSet format
            const breakingNews = extractNewsItems(newsletterData.breaking_news);
            const beyondFeed = extractNewsItems(newsletterData.beyond_the_feed);

            const teamsPayload = {
                "type": "message",
                "attachments": [
                    {
                        "contentType": "application/vnd.microsoft.card.adaptive",
                        "content": {
                            "type": "AdaptiveCard",
                            "version": "1.2",
                            "body": [
                                {
                                    "type": "TextBlock",
                                    "text": "🌍 The Climate Spectrum Newsletter",
                                    "weight": "bolder",
                                    "size": "large",
                                    "color": "good"
                                },
                                {
                                    "type": "TextBlock",
                                    "text": newsletterData.welcome,
                                    "wrap": true,
                                    "spacing": "medium"
                                },
                                {
                                    "type": "Container",
                                    "items": [
                                        {
                                            "type": "TextBlock",
                                            "text": "📰 Breaking News",
                                            "weight": "bolder",
                                            "size": "large",
                                            "color": "accent"
                                        },
                                        {
                                            "type": "FactSet",
                                            "facts": breakingNews.map((item, index) => ({
                                                "title": `${index + 1}.`,
                                                "value": item
                                            }))
                                        }
                                    ]
                                },
                                {
                                    "type": "Container",
                                    "items": [
                                        {
                                            "type": "TextBlock",
                                            "text": "🔍 Beyond the Feed",
                                            "weight": "bolder",
                                            "size": "large",
                                            "color": "accent"
                                        },
                                        {
                                            "type": "FactSet",
                                            "facts": beyondFeed.map((item, index) => ({
                                                "title": `${index + 1}.`,
                                                "value": item
                                            }))
                                        }
                                    ]
                                },
                                {
                                    "type": "Container",
                                    "items": [
                                        {
                                            "type": "TextBlock",
                                            "text": "📖 Selected Long Read",
                                            "weight": "bolder",
                                            "size": "large",
                                            "color": "accent"
                                        },
                                        {
                                            "type": "TextBlock",
                                            "text": turndown.turndown(newsletterData.selected_long_read),
                                            "wrap": true
                                        }
                                    ]
                                },
                                {
                                    "type": "TextBlock",
                                    "text": newsletterData.to_close,
                                    "wrap": true,
                                    "spacing": "medium"
                                }
                            ]
                        }
                    }
                ]
            };

            // Get webhook URL from Firebase metadata
            const webhookUrl = "https://prod-163.westeurope.logic.azure.com:443/workflows/cae23f67c0004767a0a71ea976a699d0/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=sYjYCiSuCFhqNTwSfMjm197Od5-E7MEidLBTdVv3UkA";
            if (!webhookUrl) {
                throw new Error('Teams webhook URL not configured');
            }

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(teamsPayload)
            });

            if (!response.ok) {
                throw new Error(`Teams webhook error: ${response.statusText}`);
            }

            showFeedback('Newsletter sent to Teams successfully!', 'success');
        } catch (error) {
            console.error('Error sending to Teams:', error);
            showFeedback(`Failed to send to Teams: ${error.message}`, 'error');
        }
    };

    const sendTrendingToTeams = async () => {
        try {
            if (!newsletter || !newsletter.includes('**newsletter:')) {
                throw new Error('No trending newsletter to send');
            }

            // Extract title and content
            const titleMatch = newsletter.match(/🌪️\s*\*\*newsletter:\s*(.*?)\*\*/i);
            const title = titleMatch ? titleMatch[1] : 'Trending Climate Insights';

            // Remove the title part and format the content
            let content = newsletter
                .replace(/🌪️\s*\*\*newsletter:\s*(.*?)\*\*/i, '')
                .replace(/^\s*\*\*\s*/g, '')
                .replace(/\s*\*\*\s*$/g, '')
                .replace(/---\s*$/g, '');

            // Convert markdown links to Teams-compatible format
            content = content.replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)');

            // Format date
            const today = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });

            const teamsPayload = {
                "type": "message",
                "attachments": [
                    {
                        "contentType": "application/vnd.microsoft.card.adaptive",
                        "content": {
                            "type": "AdaptiveCard",
                            "version": "1.2",
                            "body": [
                                {
                                    "type": "TextBlock",
                                    "text": `🌍 ${title}`,
                                    "weight": "bolder",
                                    "size": "large",
                                    "color": "good"
                                },
                                {
                                    "type": "TextBlock",
                                    "text": `Climate Spectrum Trending Newsletter: ${today}`,
                                    "wrap": true,
                                    "spacing": "small",
                                    "size": "small"
                                },
                                {
                                    "type": "TextBlock",
                                    "text": content,
                                    "wrap": true,
                                    "spacing": "medium"
                                }
                            ]
                        }
                    }
                ]
            };

            // Get webhook URL
            const webhookUrl = "https://prod-163.westeurope.logic.azure.com:443/workflows/cae23f67c0004767a0a71ea976a699d0/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=sYjYCiSuCFhqNTwSfMjm197Od5-E7MEidLBTdVv3UkA";
            if (!webhookUrl) {
                throw new Error('Teams webhook URL not configured');
            }

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(teamsPayload)
            });

            if (!response.ok) {
                throw new Error(`Teams webhook error: ${response.statusText}`);
            }

            showFeedback('Trending newsletter sent to Teams successfully!', 'success');
        } catch (error) {
            console.error('Error sending to Teams:', error);
            showFeedback(`Failed to send to Teams: ${error.message}`, 'error');
        }
    };

    const publishToWordPress = async () => {
        try {
            // Get WordPress credentials from metadata (safer than hardcoding)
            const wordpressConfig = metadata.wordpressConfig || {};
            const { siteUrl, username, appPassword } = wordpressConfig;

            if (!siteUrl || !username || !appPassword) {
                throw new Error('WordPress credentials not configured in metadata');
            }
            console.table(wordpressConfig);
            // Get newsletter content
            const repairedJson = cleanAndRepairJson(newsletter);
            const newsletterData = JSON.parse(repairedJson);

            // Validate content
            validateNewsletterData(newsletterData);

            // Format date for title
            const today = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });

            // Create HTML content for WordPress
            const htmlContent = `
                <p>${newsletterData.welcome}</p>
                
                <h2>📰 Breaking News</h2>
                ${newsletterData.breaking_news}
                
                <h2>🔍 Beyond the Feed</h2>
                ${newsletterData.beyond_the_feed}
                
                <h2>📖 Selected Long Read</h2>
                ${newsletterData.selected_long_read}
                
                <p>${newsletterData.to_close}</p>
            `;

            // Create authorization header
            const auth = btoa(`${username}:${appPassword}`);

            // Post to WordPress
            const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts`, {
                method: 'POST',
                headers: {
                    'Authorization': `Basic ${auth}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    title: `The Climate Spectrum Newsletter: ${today}`,
                    content: htmlContent,
                    status: 'publish',  // Set to 'draft' for review or 'publish' to publish immediately
                    categories: [metadata.wordpressConfig?.categoryId || 1] // Default to category ID 1 if not specified
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`WordPress error: ${errorData.message || response.statusText}`);
            }

            const result = await response.json();
            console.log('Posted to WordPress successfully:', result);

            // Show success message with link to edit the post
            showFeedback(
                <>
                    Newsletter published to WordPress as draft.
                    <a
                        href={`${siteUrl}/wp-admin/post.php?post=${result.id}&action=edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: '8px', color: 'white', textDecoration: 'underline' }}
                    >
                        Edit Post
                    </a>
                </>,
                'success'
            );

        } catch (error) {
            console.error('Error publishing to WordPress:', error);
            showFeedback(`Failed to publish to WordPress: ${error.message}`, 'error');
        }
    };

    // Helper function to extract news items from HTML
    const extractNewsItems = (html) => {
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(html);
        const items = Array.from(div.querySelectorAll('li'));
        return items.map(item => {
            const link = item.querySelector('a');
            if (link) {
                return `[${link.textContent}](${link.href})${item.textContent.replace(link.textContent, '')}`;
            }
            return item.textContent;
        });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(newsletter);
    };

    const renderNewsletterContent = () => {
        try {
            const repairedJson = cleanAndRepairJson(newsletter);
            const data = JSON.parse(repairedJson);

            if (!data || typeof data !== 'object') {
                throw new Error('Invalid newsletter data structure');
            }
            return (
                <div>
                    <Typography variant="h5" gutterBottom>
                        Welcome
                    </Typography>
                    <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.welcome) }}
                        paragraph
                    />

                    <Typography variant="h5" gutterBottom>
                        Breaking News
                    </Typography>
                    <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.breaking_news) }}
                        paragraph
                    />

                    <Typography variant="h5" gutterBottom>
                        Beyond the Feed
                    </Typography>
                    <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.beyond_the_feed) }}
                        paragraph
                    />

                    <Typography variant="h5" gutterBottom>
                        Selected Long Read
                    </Typography>
                    <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.selected_long_read) }}
                        paragraph
                    />

                    <Typography variant="h5" gutterBottom>
                        To Close
                    </Typography>
                    <Typography
                        variant="body1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.to_close) }}
                        paragraph
                    />
                </div>
            );
        } catch (error) {
            console.error('Error parsing newsletter:', error);
            return (
                <Typography color="error">
                    Error parsing newsletter content: {error.message}
                    <pre style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: '#f5f5f5',
                        padding: '1rem',
                        marginTop: '1rem'
                    }}>
                        {newsletter}
                    </pre>
                </Typography>
            );
        }
    };

    const renderTrendingNewsletterContent = () => {
        try {
            // For trending newsletter, we don't need to parse JSON since it's already in the expected format
            if (!newsletter) {
                return null;
            }

            // Split the content to find the newsletter title and body
            const titleMatch = newsletter.match(/🌪️\s*\*\*newsletter:\s*(.*?)\*\*/i);
            const title = titleMatch ? titleMatch[1] : 'Trending Newsletter';

            // Remove the title part and any leading/trailing markup
            let content = newsletter
                .replace(/🌪️\s*\*\*newsletter:\s*(.*?)\*\*/i, '')
                .replace(/^\s*\*\*\s*/g, '')
                .replace(/\s*\*\*\s*$/g, '')
                .replace(/---\s*$/g, '');

            // Convert markdown links to HTML
            content = content.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

            // Convert line breaks to paragraphs
            const paragraphs = content.split('\n\n').filter(p => p.trim());

            return (
                <div>
                    <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
                        {title}
                    </Typography>

                    {paragraphs.map((paragraph, index) => (
                        <Typography
                            key={index}
                            variant="body1"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(paragraph) }}
                            paragraph
                            sx={{ mb: 2 }}
                        />
                    ))}
                </div>
            );
        } catch (error) {
            console.error('Error parsing trending newsletter:', error);
            return (
                <Typography color="error">
                    Error parsing trending newsletter content: {error.message}
                    <pre style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        backgroundColor: '#f5f5f5',
                        padding: '1rem',
                        marginTop: '1rem'
                    }}>
                        {newsletter}
                    </pre>
                </Typography>
            );
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                    <Button
                        variant="contained"
                        onClick={generateNewsletter}
                        disabled={loading}
                        style={{ marginBottom: '20px', marginRight: '10px' }}
                        id='generate-newsletter-button'
                    >
                        {loading ? <CircularProgress size={24} /> : 'Generate Newsletter'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={generateTrendingNewsletter}
                        disabled={loading}
                        style={{ marginBottom: '20px', marginRight: '10px' }}
                        id='generate-trending-newsletter'
                    >
                        {loading ? <CircularProgress size={24} /> : 'Generate Trending Newsletter'}
                    </Button>
                    {newsletter && (
                        <>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={sendNewsletter}
                                disabled={sendingEmail}
                                style={{ marginBottom: '20px' }}
                                id="send-newsletter-button"
                            >
                                {sendingEmail ? <CircularProgress size={24} /> : 'Send Newsletter'}
                            </Button>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={newsletter.includes('"welcome":') ? sendToTeams : sendTrendingToTeams}
                                style={{ marginBottom: '20px', marginLeft: '10px' }}
                                id="send-teams-button"
                            >
                                Send to Teams
                            </Button>

                            <Button
                                variant="contained"
                                color="success"
                                onClick={publishToWordPress}
                                style={{ marginBottom: '20px', marginLeft: '10px' }}
                                id="publish-wordpress-button"
                            >
                                Publish to WordPress
                            </Button>

                            <Paper
                                elevation={3}
                                sx={{
                                    padding: '20px',
                                    marginTop: '20px',
                                    '& a': {
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }
                                }}
                            >
                                {/* Use the appropriate rendering function based on the newsletter format */}
                                {newsletter.includes('"welcome":') ? renderNewsletterContent() : renderTrendingNewsletterContent()}
                                <Button
                                    variant="outlined"
                                    onClick={copyToClipboard}
                                    style={{ marginTop: '20px' }}
                                >
                                    Copy to Clipboard
                                </Button>
                            </Paper>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
export default NewsletterGenerator;